import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Target, Loader2, MapPin, Briefcase, Award } from "lucide-react";

const EXAMS = ["JEE Advanced", "JEE Main", "NEET", "CUET"];
const CATEGORIES = ["General", "OBC", "EWS", "SC", "ST"];
const bucketStyle = {
  Safe: "border-emerald-400/30 bg-emerald-400/5 text-emerald-300",
  Target: "border-cyan-400/30 bg-cyan-400/5 text-cyan-300",
  Dream: "border-amber-400/30 bg-amber-400/5 text-amber-300",
};

export default function CollegePredictorPage() {
  const [params] = useSearchParams();
  const [exam, setExam] = useState(params.get("exam") || "JEE Main");
  const [rank, setRank] = useState(params.get("rank") && !isNaN(Number(params.get("rank"))) ? Number(params.get("rank")) : 15000);
  const [category, setCategory] = useState("General");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const predict = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/predict/college", { exam, rank: Number(rank), category });
      setResults(data.matches);
      if (data.matches.length === 0) toast.info("No matches found. Try a higher rank range.");
    } catch (e) {
      toast.error("Prediction failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const grouped = { Safe: [], Target: [], Dream: [] };
  (results || []).forEach((r) => grouped[r.bucket]?.push(r));

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-8 py-8">
      <Header icon={Target} eyebrow="AI College Predictor"
        title="Find colleges you can get into" desc="Enter your rank & category to see Dream, Target and Safe colleges." />

      <div className="mb-6 rounded-xl border border-amber-400/20 bg-amber-400/5 px-4 py-2.5 text-xs text-amber-300" data-testid="prediction-disclaimer">
        These are statistical estimates based on historical trends — not a guarantee of admission. Always verify official cutoffs.
      </div>

      <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-6 grid sm:grid-cols-3 gap-5">
        <div>
          <label className="text-sm font-medium text-slate-300">Exam</label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="predictor-exam-select">
            {EXAMS.map((e) => (
              <button key={e} onClick={() => setExam(e)} data-testid={`exam-opt-${e.replace(/\s+/g, "-")}`}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                  exam === e ? "bg-cyan-400 text-[#041016]" : "bg-white/5 text-slate-400 hover:text-white"
                }`}>{e}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-300">Category</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button key={c} onClick={() => setCategory(c)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                  category === c ? "bg-indigo-400 text-[#041016]" : "bg-white/5 text-slate-400 hover:text-white"
                }`}>{c}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-300">Your Rank</label>
          <input data-testid="predictor-rank-input" type="number" value={rank} onChange={(e) => setRank(e.target.value)}
            className="mt-2 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50 font-mono-data" />
        </div>
        <div className="sm:col-span-3">
          <button data-testid="predictor-calculate-btn" onClick={predict} disabled={loading}
            className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-7 py-3 text-sm font-bold text-[#041016] transition-transform hover:scale-105 disabled:opacity-50">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Predicting...</> : <><Target className="h-4 w-4" /> Predict Colleges</>}
          </button>
        </div>
      </div>

      {results && (
        <div className="mt-8 space-y-8">
          {["Dream", "Target", "Safe"].map((bucket) =>
            grouped[bucket].length > 0 ? (
              <div key={bucket}>
                <h3 className="font-display text-lg font-semibold mb-3 flex items-center gap-2">
                  <span className={`rounded-full border px-3 py-1 text-xs ${bucketStyle[bucket]}`}>{bucket}</span>
                  <span className="text-slate-400 text-sm">{grouped[bucket].length} colleges</span>
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {grouped[bucket].map((c, i) => (
                    <motion.div key={c.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                      data-testid="college-match-card" className={`rounded-2xl border p-5 ${bucketStyle[bucket]}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-display font-semibold text-white">{c.name}</p>
                          <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" /> {c.city}</p>
                        </div>
                        <span className="font-mono-data text-xl font-bold">{c.probability}%</span>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                        <span className="text-slate-400 flex items-center gap-1"><Award className="h-3 w-3" /> NIRF #{c.nirf_rank}</span>
                        <span className="text-slate-400 flex items-center gap-1"><Briefcase className="h-3 w-3" /> ₹{(c.avg_package / 100000).toFixed(1)}L avg</span>
                      </div>
                      <p className="mt-2 text-[11px] text-slate-500 font-mono-data">Cutoff (est): {c.cutoff.toLocaleString()}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            ) : null
          )}
        </div>
      )}
    </div>
  );
}

export const Header = ({ icon: Icon, eyebrow, title, desc }) => (
  <div className="mb-8">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="h-4 w-4 text-cyan-400" />
      <span className="font-mono-data text-xs uppercase tracking-[0.2em] text-cyan-400">{eyebrow}</span>
    </div>
    <h1 className="font-display text-3xl font-extrabold tracking-tight">{title}</h1>
    {desc && <p className="mt-2 text-slate-400">{desc}</p>}
  </div>
);
